import { Field, Form, Formik } from 'formik';
import { useRef } from 'react';
import * as Yup from 'yup';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import Label from '@/components/Label';
import { SelectFormField } from '../../components/formFields/SelectFormField';
import { AreaField } from '../../components/formFields/AreaField';
import { InstructionTypes } from './constants';
import { useQuery } from 'react-query';
import { apiService } from '../../utils/api';
import { RadioGroup, RadioGroupItem } from '@/components/ui/RadioGroup';

const INSTRUCTIONS_WITH_CONTENT = ['HEA', 'TEX', 'DSC', 'CN'];

const InstructionDialog = ({
  visible,
  onClose,
  formProps = null,
  onSubmit,
}: {
  visible?: any;
  onClose?: any;
  formProps?: any;
  onSubmit?: any;
}) => {
  const formRef = useRef(null);

  const { data } = useQuery({
    queryKey: ['instructions-types'],
    queryFn: async () => {
      const { data } = await apiService.get(`/api/v1/instruction-template/instruction/`);

      return data;
    },
    refetchOnWindowFocus: false,
  });

  const ValidationSchema = Yup.object().shape({
    parameters: Yup.object().shape({
      content: Yup.string().when('instruction_type', {
        is: (value) => {
          return INSTRUCTIONS_WITH_CONTENT.includes(value);
        },
        then: () => Yup.string().required(),
        otherwise: () => Yup.string().nullable(),
      }),
    }),
    instruction_type: Yup.string().nullable().required('Povinné pole'),
  });

  const onSubmitButton = () => {
    if (!formRef?.current) return null;
    formRef.current.submitForm();
  };

  const options = data?.map((value) => {
    return { title: value.title, id: value.shortcut };
  });

  const headerOptions = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map((key) => {
    return { title: key, id: key };
  });

  return (
    <Dialog open={visible} onOpenChange={onClose} modal={false}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{formProps ? 'Editace instrukce' : 'Vytvoření instrukce'}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Formik
            innerRef={formRef}
            enableReinitialize={true}
            validationSchema={ValidationSchema}
            initialValues={{
              instruction_type: formProps?.instruction_type || null,
              parameters: {
                content: formProps?.parameters?.content || null,
                header_type: formProps?.parameters?.header_type || null,
                is_simple: formProps?.parameters?.is_simple ?? false,
              },
            }}
            onSubmit={(values) => {
              onSubmit(formProps?.id, values);
            }}
          >
            {({ values, setFieldValue }) => (
              <>
                <Form>
                  <div>
                    <>
                      <Label name="instruction_type" title="Typ instrukce" required />
                      <SelectFormField
                        name="instruction_type"
                        placeholder="Typ instrukce"
                        data={options ?? []}
                        onValueChange={(value) => {
                          if (value === InstructionTypes.SO) {
                            setFieldValue('parameters.is_simple', true);
                          } else {
                            setFieldValue('parameters.is_simple', false);
                          }
                        }}
                      />
                    </>

                    {values.instruction_type === InstructionTypes.HEA && (
                      <>
                        <Label name="parameters.header_type" title="Typ nadpisu" required />
                        <SelectFormField
                          name="parameters.header_type"
                          placeholder="Typ nadpisu"
                          data={headerOptions}
                        />
                      </>
                    )}

                    {INSTRUCTIONS_WITH_CONTENT.includes(values.instruction_type) && (
                      <>
                        <Label name="parameters.content" title="Text" required />
                        <Field
                          name="parameters.content"
                          value={values.parameters.content || null}
                          placeholder="Text"
                          component={AreaField}
                        />
                      </>
                    )}

                    {values.instruction_type === InstructionTypes.SO && (
                      <Field name="parameters.is_simple">
                        {() => (
                          <div className="mt-2">
                            <Label
                              name="parameters.is_simple"
                              title="Jednoduché nebo plné řešení"
                            />
                            <RadioGroup
                              value={values.parameters.is_simple ? 'simple' : 'full'}
                              disabled
                            >
                              <div className="flex items-center gap-2">
                                <RadioGroupItem value="simple" id="is_simple_true" />
                                <label
                                  htmlFor="is_simple_true"
                                  className="text-sm text-neutral-500"
                                >
                                  Jednoduché řešení
                                </label>
                              </div>
                              <div className="flex items-center gap-2">
                                <RadioGroupItem value="full" id="is_simple_false" />
                                <label
                                  htmlFor="is_simple_false"
                                  className="text-sm text-neutral-500"
                                >
                                  Plné řešení
                                </label>
                              </div>
                            </RadioGroup>
                          </div>
                        )}
                      </Field>
                    )}
                  </div>
                </Form>
              </>
            )}
          </Formik>
        </DialogBody>
        <DialogFooter>
          <Button
            key={1}
            variant="outline"
            data-testid="instruction-dialog-close-btn"
            onClick={() => onClose()}
          >
            Zavřít
          </Button>
          <Button
            key={2}
            data-testid="instruction-dialog-confirm-btn"
            onClick={() => onSubmitButton()}
          >
            Potvrdit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InstructionDialog;
